import React, { useEffect } from "react";
import {
  Box,
  Stack,
  Typography,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import LayerdIcon from "../../../common/LayerdIcon";
import { DETECTION_ICONS } from "../../../../icons/detectionLucideIcons";
import ReactCountryFlag from "react-country-flag";
import copy from "copy-to-clipboard";
import { Clock, Globe, Link2, Shield, ScanSearch, Flame, Hash, Activity, Target } from "lucide-react";
import { formatDate } from "../../../../utils/format";
import { getResStatusString } from "../../application/analytics/common";
import useEvent from "../../../../hooks/user/useEvent";
import { displayUserAgent } from "../../../../utils/display";
import { buildEventDrawerData } from "./eventDrawerData";
import { buildBotEventDrawerSections, resolveDrawerEventType, getBotEventDetectors } from "./botEventDrawerData";
import BotEventDrawerBody from "./BotEventDrawerBody";
import WafEventDrawerShell from "./WafEventDrawerShell";
import SmartParseDrawerHeader from "./v3/SmartParseDrawerHeader";
import SmartParseRawJsonViewer from "./v3/SmartParseRawJsonViewer";
import ViewSimilarEventsPanel from "./v3/ViewSimilarEventsPanel";
import { buildFirewallSimilarEventShape } from "./firewallSimilarEvents";
import { EventAssessGrid, SmartParseSectionCard as SectionCard, NetworkFieldRow, DrawerFieldStack, DrawerNumberedList, DrawerHeadersPanel, NetworkInformationPanel, IpReputationPanel, toDrawerFieldValue, drawerFieldsHaveContent, hasAbuseReputationData, isDrawerValuePresent } from "./v3/smartParseDrawerUi";
import RequestHeadersSection from "./v3/RequestHeadersSection";
import AbuseConfidenceScoreCard from "./v3/AbuseConfidenceScoreCard";
import {
  PRIMARY_COLOR,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  BORDER_COLOR,
  baseFontStyle,
  secondaryButtonStyle,
  getStatusConfig,
  getRiskLevelConfig,
  ConfidenceGauge,
  isBlockedAction,
  BLOCKED_STATUS_FG,
  drawerSeverityPalette,
} from "./wafEventDrawerShared";

function assessVerdictValueColor(verdict) {
  const key = String(verdict || "").toLowerCase();
  if (key === "malicious" || key === "attack") return "#B91C1C";
  if (key === "suspicious" || key === "mitigation") return "#7C3AED";
  return TEXT_PRIMARY;
}

function assessRiskLevelValueColor(riskLevel) {
  const text = String(riskLevel || "").trim();
  if (!text || text === "(Empty)") return TEXT_SECONDARY;
  const palette = drawerSeverityPalette(text);
  return palette.fg;
}

function legacyRequestPath(eventData) {
  const uri = eventData.request_uri || eventData.uri || eventData.path || "";
  if (!uri) return null;
  const queryIndex = uri.indexOf("?");
  return queryIndex > -1 ? uri.substring(0, queryIndex) : uri;
}

function resolveDrawerField(primary, ...fallbacks) {
  const candidates = [primary, ...fallbacks];
  for (const candidate of candidates) {
    if (isDrawerValuePresent(candidate)) return candidate;
  }
  return null;
}

const getEventTypeConfig = (eventType) => {
  switch (eventType) {
    case "waf":
      return { 
        title: "WAF Event",
        icon: DETECTION_ICONS.wafDetection,
        description: "Web Application Firewall detection based on security rules"
      };
    case "smart_parse":
      return { 
        title: "Smart Parse Event",
        icon: DETECTION_ICONS.wafDetection,
        description: "Layerd AI Smart Parse detection with detailed attack analysis"
      };
    case "firewall":
      return { 
        title: "Firewall Event",
        icon: DETECTION_ICONS.detection,
        description: "Firewall rule match based on custom access rules"
      };
    case "bot":
    case "bot_management":
      return { 
        title: "Bot Event",
        icon: DETECTION_ICONS.detection,
        description: "Automated traffic detection and bot management"
      };
    case "ratelimit":
      return { 
        title: "Rate Limit Event",
        icon: DETECTION_ICONS.detection,
        description: "Rate limiting enforcement to prevent abuse"
      };
    default:
      return { 
        title: "Security Event",
        icon: DETECTION_ICONS.detection,
        description: "Security event detected"
      };
  }
};

const getSignalSeverityConfig = (severity) => {
  const level = (severity || "").toString().trim().toLowerCase();
  switch (level) {
    case "critical":
      return { label: "Critical", color: "#B91C1C", bgColor: "#FEE2E2", borderColor: "#FCA5A5" };
    case "high":
      return { label: "High", color: "#9A3412", bgColor: "#FFEDD5", borderColor: "#FDBA74" };
    case "medium":
      return { label: "Medium", color: "#92400E", bgColor: "#FEF3C7", borderColor: "#FCD34D" };
    case "low":
      return { label: "Low", color: "#166534", bgColor: "#DCFCE7", borderColor: "#86EFAC" };
    default:
      return { label: severity || "—", color: TEXT_SECONDARY, bgColor: "#F3F4F6", borderColor: "#E5E7EB" };
  }
};

const EventDetailDrawer = ({ open, onClose, event, event_Type = "waf", onFilterSimilar }) => {
  const [snackMessage, setSnackMessage] = React.useState("");
  const [eventType, setEventType] = React.useState(event_Type);
  const [snackOpen, setSnackOpen] = React.useState(false);
  // Per-attack toggle state for evidence table visibility
  const [expandedEvidenceMap, setExpandedEvidenceMap] = React.useState({});
  const [requestHeadersExpanded, setRequestHeadersExpanded] = React.useState(false);
  const {
    fwEventLoading,
    botEventLoading,
    rlEventLoading,
  } = useEvent();

  const isEventDetailLoading =
    open &&
    ((eventType === "firewall" && fwEventLoading) ||
      (eventType === "bot" && botEventLoading) ||
      (eventType === "ratelimit" && rlEventLoading));

  useEffect(() => {
    if (!open) return;
    setEventType(resolveDrawerEventType(event_Type, event));
  }, [open, event_Type, event]);

  useEffect(() => {
    // Reset per-event UI toggles to avoid showing stale expanded state.
    if (open) {
      setExpandedEvidenceMap({});
      setRequestHeadersExpanded(false);
    }
  }, [open, event?.id, event?.request_id]);

  if (!event) return null;

  if (event_Type === "smart_parse" || event?.waf_type === "smart_parse") {
    return null;
  }

  const isBotDrawer = resolveDrawerEventType(event_Type, event) === "bot";

  const handleCopy = (value) => {
    copy(value);
    setSnackMessage("Copied to clipboard!");
    setSnackOpen(true);
    setTimeout(() => setSnackOpen(false), 2000);
  };

  if (isBotDrawer) {
    const botSections = buildBotEventDrawerSections(event);
    const botEventConfig = getEventTypeConfig("bot");

    return (
      <>
        <WafEventDrawerShell
          open={open}
          onClose={onClose}
          title={botEventConfig.title}
          loading={isEventDetailLoading}
          headerVariant="hero"
          headerContent={(
            <SmartParseDrawerHeader
              embedded
              title={botEventConfig.title}
              eventId={botSections.headerEventId}
              method={botSections.headerMethod}
              path={botSections.headerPath}
              onCopyEventId={handleCopy}
            />
          )}
        >
          <BotEventDrawerBody event={event} onCopy={handleCopy} />
        </WafEventDrawerShell>
        {snackOpen && (
          <Box
            sx={{
              position: "fixed",
              bottom: 80,
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: "#1A1A1A",
              color: "#fff",
              px: 2,
              py: 1,
              borderRadius: "8px",
              fontSize: 14,
              zIndex: 1400,
            }}
          >
            {snackMessage}
          </Box>
        )}
      </>
    );
  }

  const eventKey = event?.request_id || event?.id;
  const isSmartParseEvent = false;
  const displayEvent = event;
  const eventData = displayEvent || {};
  const attacks = isSmartParseEvent && Array.isArray(eventData.attacks) && eventData.attacks.length > 0
    ? eventData.attacks
    : (Array.isArray(eventData.attack_types) ? eventData.attack_types : []);
  const attackDisplayList = isSmartParseEvent
    ? attacks.flatMap((attack) => {
        if (typeof attack === "string") {
          return attack
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);
        }
        const attackType = attack?.type || attack?.attack_type;
        if (typeof attackType === "string" && attackType.includes(",")) {
          return attackType
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean)
            .map((item) => ({
              ...attack,
              type: item,
              attack_type: item,
            }));
        }
        return [attack];
      })
    : attacks;
  const categories = Array.isArray(eventData.categories_detected) ? eventData.categories_detected : [];

  const getSmartParseStatus = () => {
    const action = eventData.action?.toLowerCase();
    if (action === "block" || action === "blocked") return "Blocked";
    if (action === "challenge" || action === "challenged") return "Challenged";
    if (action === "detect" || action === "detected" || action === "passed") return "Passed";
    const resStatus = eventData.res_status;
    if (resStatus === 403 || resStatus === 429) return "Blocked";
    if (resStatus === 503) return "Challenged";
    return "Passed";
  };

  const getRateLimitStatus = () => {
    const resStatus = eventData.res_status || eventData.resStatus;
    if (eventType === "ratelimit" && resStatus === 429) {
      return "Rate Limited";
    }
    return getResStatusString(resStatus);
  };
  
  const rawStatusString = isSmartParseEvent ? getSmartParseStatus() : (eventType === "ratelimit" ? getRateLimitStatus() : getResStatusString(eventData.res_status || eventData.resStatus));
  const statusString = eventType === "bot" && rawStatusString === "Passed" ? "Detect" : rawStatusString;
  const statusConfig = getStatusConfig(statusString);
  const eventConfig = getEventTypeConfig(eventType);
  const sourceIp = eventData.src_ip || eventData.ip;
  const countryIso = eventData.country_iso_code || eventData.country;
  const countryName = eventData.country_name || eventData.country;
  const drawerData = buildEventDrawerData({
    event,
    eventType,
  });
  const remediationRecommendations = drawerData.remediationRecommendations || [];
  const remediationByIntent = drawerData.remediationByIntent || [];
  const intentTagsRich = drawerData.intentTagsRich || [];
  const campaignDrawer = drawerData.campaign;

  const handleFilterSimilar = (filterType) => {
    if (onFilterSimilar) {
      onFilterSimilar(filterType, event);
    }
    onClose();
  };

  const getScopeChipColors = (scope) => {
    switch (scope) {
      case "query_string":
        return { backgroundColor: "#FEF3C7", color: "#92400E" };
      case "path":
        return { backgroundColor: "#DBEAFE", color: "#1E40AF" };
      case "header":
        return { backgroundColor: "#E0E7FF", color: "#4338CA" };
      default:
        return { backgroundColor: "#F3F4F6", color: TEXT_SECONDARY };
    }
  };

  const parseScopePrefix = (text) => {
    if (!text || typeof text !== "string") return null;
    const scopeMatch = text.match(/^\[([^\]]+)\]\s*/);
    return scopeMatch ? scopeMatch[1] : null;
  };

  const stripScopePrefix = (text) => {
    if (!text || typeof text !== "string") return "";
    const scopeMatch = text.match(/^\[([^\]]+)\]\s*/);
    return scopeMatch ? text.replace(scopeMatch[0], "") : text;
  };

  const normalizeEvidence = (ev) => {
    if (!ev || typeof ev !== "object") {
      return { scope: "—", patternText: "—", position: "—", score: undefined, positionNum: Number.POSITIVE_INFINITY, scoreNum: Number.NEGATIVE_INFINITY };
    }

    const rawSignal =
      ev.attack_signals ??
      ev.pattern ??
      ev.description ??
      ev.matched ??
      "";

    const scopeFromSignalPrefix = parseScopePrefix(rawSignal);
    const scope = ev.scope ?? scopeFromSignalPrefix ?? "—";

    const patternText = stripScopePrefix(rawSignal) || "—";
    const position = ev.position !== undefined ? ev.position : ev.index;

    const scoreNum = ev.score !== undefined ? Number(ev.score) : ev.attack_score !== undefined ? Number(ev.attack_score) : Number.NEGATIVE_INFINITY;
    const positionNum =
      position !== undefined && position !== null && position !== "" && !Number.isNaN(Number(position))
        ? Number(position)
        : Number.POSITIVE_INFINITY;

    return { scope, patternText, position: position !== undefined && position !== null ? position : "—", score: scoreNum === Number.NEGATIVE_INFINITY ? undefined : scoreNum, positionNum, scoreNum };
  };

  const headerMethod = (eventData.method || eventData.http_method || "").toUpperCase();
  const headerPath = eventData.uri || eventData.path || eventData.url_path || eventData.request_uri || "";
  const headerEventId = eventData.request_id || eventData.id || eventKey;

  const formatFirewallAction = () => {
    const action = (event.action || event.waf_action || "").toLowerCase();
    if (action === "block" || action === "hard_block") return "Block";
    if (action === "allow" || action === "pass") return "Allow";
    if (action === "log" || action === "monitor") return "Log";
    if (action === "soft_block" || action === "challenge") return "Challenge";
    return event.action || event.waf_action || "—";
  };

  const firewallAssessCells = eventType === "firewall" ? [
    {
      key: "timestamp",
      label: "Timestamp",
      value: event.timestamp ? formatDate(event.timestamp) : null,
      icon: Clock,
    },
    {
      key: "hostname",
      label: "Hostname",
      value: event.host_name,
      icon: Globe,
      valueColor: PRIMARY_COLOR,
    },
    {
      key: "uri",
      label: "URI",
      value: headerPath,
      icon: Link2,
      monospace: true,
    },
    event.verdict
      ? {
        key: "verdict",
        label: "Verdict",
        value: event.verdict.charAt(0).toUpperCase() + event.verdict.slice(1),
        icon: ScanSearch,
        valueColor: event.verdict === "attack" ? "#B91C1C" : "#065F46",
      }
      : null,
    event.risk_level && event.risk_level !== "unknown"
      ? {
        key: "risk",
        label: "Risk",
        value: getRiskLevelConfig(event.risk_level).label,
        icon: Flame,
        valueColor: getRiskLevelConfig(event.risk_level).color,
      }
      : null,
    (event.action || event.waf_action)
      ? { key: "action", label: "Action", value: formatFirewallAction(), icon: Shield }
      : null,
    event.request_id
      ? { key: "request_id", label: "Request ID", value: event.request_id, icon: Hash, monospace: true, copyable: true }
      : null,
  ].filter(Boolean) : [];

  const isLegacyEngineDrawer = eventType === "ratelimit" || eventType === "bot";

  const rateLimitAssessCells = eventType === "ratelimit" ? [
    {
      key: "timestamp",
      label: "Timestamp",
      value: (eventData.timestamp || eventData.ts) ? formatDate(eventData.timestamp || eventData.ts) : null,
      icon: Clock,
    },
    {
      key: "hostname",
      label: "Hostname",
      value: eventData.host_name || eventData.host,
      icon: Globe,
      valueColor: PRIMARY_COLOR,
    },
    {
      key: "verdict",
      label: "Verdict",
      value: eventData.verdict,
      icon: ScanSearch,
      valueColor: assessVerdictValueColor(eventData.verdict),
    },
    {
      key: "action",
      label: "Action",
      value: eventData.action,
      icon: Shield,
      valueColor: isBlockedAction(eventData.action) ? BLOCKED_STATUS_FG : TEXT_PRIMARY,
    },
    eventData.mode
      ? { key: "mode", label: "Mode", value: eventData.mode, icon: Activity }
      : null,
    eventData.rule_id
      ? {
        key: "ruleId",
        label: "Rule ID",
        value: eventData.rule_id,
        icon: Hash,
        monospace: true,
        copyable: true,
      }
      : null,
    eventData.rule_name
      ? { key: "ruleName", label: "Rule Name", value: eventData.rule_name, icon: Target }
      : null,
    eventData.limit != null && eventData.period_s != null
      ? {
        key: "threshold",
        label: "Threshold",
        value: `${eventData.limit} request${eventData.limit === 1 ? "" : "s"} / ${eventData.period_s}s`,
        icon: Activity,
      }
      : null,
    (eventData.blocked_bool !== undefined || eventData.sd_rate_limited != null || eventData.resStatus != null)
      ? {
        key: "requestBlocked",
        label: "Request Blocked",
        value: (eventData.blocked_bool === true || eventData.sd_rate_limited === 1 || eventData.resStatus === 429)
          ? "True"
          : "False",
        icon: Shield,
      }
      : null,
    eventData.bucket_key
      ? {
        key: "bucketKey",
        label: "Bucket Key",
        value: eventData.bucket_key,
        icon: Hash,
        monospace: true,
        copyable: true,
      }
      : null,
  ].filter(Boolean) : [];

  const botLegacyAssessCells = eventType === "bot" ? [
    {
      key: "timestamp",
      label: "Timestamp",
      value: eventData.timestamp ? formatDate(eventData.timestamp) : null,
      icon: Clock,
    },
    {
      key: "hostname",
      label: "Hostname",
      value: eventData.host_name || eventData.host,
      icon: Globe,
      valueColor: PRIMARY_COLOR,
    },
    {
      key: "verdict",
      label: "Verdict",
      value: drawerData.detectionSummary.verdict,
      icon: ScanSearch,
      valueColor: assessVerdictValueColor(drawerData.detectionSummary.verdict),
    },
    {
      key: "riskLevel",
      label: "Risk Level",
      value: drawerData.detectionSummary.riskLevel,
      icon: Flame,
      valueColor: assessRiskLevelValueColor(drawerData.detectionSummary.riskLevel),
    },
    {
      key: "confidence",
      label: "Confidence",
      value: drawerData.detectionSummary.confidence,
      icon: Activity,
    },
    {
      key: "action",
      label: "Action",
      value: drawerData.detectionSummary.action,
      icon: Shield,
      valueColor: isBlockedAction(drawerData.detectionSummary.action)
        ? BLOCKED_STATUS_FG
        : TEXT_PRIMARY,
    },
    {
      key: "wafType",
      label: "WAF Type",
      value: drawerData.detectionSummary.wafType,
      icon: DETECTION_ICONS.wafDetection,
    },
    {
      key: "detectedIntents",
      label: "Detected Intents",
      value: drawerData.detectionSummary.detectedIntents.length > 0
        ? drawerData.detectionSummary.detectedIntents.join(", ")
        : null,
      icon: ScanSearch,
    },
    eventData.rule_id
      ? {
        key: "ruleId",
        label: "Rule ID",
        value: eventData.rule_id,
        icon: Hash,
        monospace: true,
        copyable: true,
      }
      : null,
    eventData.rule_name
      ? {
        key: "ruleName",
        label: "Rule Name",
        value: eventData.rule_name,
        icon: Target,
      }
      : null,
  ].filter(Boolean) : [];

  const legacyEngineAssessCells = eventType === "ratelimit"
    ? rateLimitAssessCells
    : botLegacyAssessCells;

  const legacyQueryParamValue = isLegacyEngineDrawer && Array.isArray(event.query) && event.query.length > 0
    ? event.query.map((param) => `${param.key}=${param.value}`).join(", ")
    : null;

  const rateLimitRequestFields = [
    {
      key: "host",
      label: "Host",
      value: toDrawerFieldValue(drawerData.requestDetails.host),
      valueColor: PRIMARY_COLOR,
    },
    {
      key: "uri",
      label: "Requested URI",
      value: toDrawerFieldValue(drawerData.requestDetails.requestedUri),
      monospace: true,
    },
    {
      key: "method",
      label: "Method",
      value: toDrawerFieldValue(eventData.method),
    },
    resolveDrawerField(event.ua, drawerData.requestDetails.userAgent)
      ? {
        key: "userAgent",
        label: "User Agent",
        value: toDrawerFieldValue(displayUserAgent(event.ua || drawerData.requestDetails.userAgent)),
      }
      : null,
  ].filter(Boolean);

  const botLegacyRequestFields = [
    {
      key: "host",
      label: "Host",
      value: toDrawerFieldValue(drawerData.requestDetails.host),
      valueColor: PRIMARY_COLOR,
    },
    {
      key: "uri",
      label: "Requested URI",
      value: toDrawerFieldValue(drawerData.requestDetails.requestedUri),
      monospace: true,
    },
    {
      key: "method",
      label: "Method",
      value: toDrawerFieldValue(eventData.method),
    },
    {
      key: "path",
      label: "Path",
      value: toDrawerFieldValue(legacyRequestPath(eventData)),
      monospace: true,
    },
    {
      key: "userAgent",
      label: "User Agent",
      value: toDrawerFieldValue(displayUserAgent(event.ua || drawerData.requestDetails.userAgent)),
    },
    {
      key: "ja3Fingerprint",
      label: "JA3 Fingerprint",
      value: toDrawerFieldValue(event.ja3_hash || event.ja3 || drawerData.requestDetails.ja3Hash),
      monospace: true,
      copyable: true,
    },
    {
      key: "httpProtocol",
      label: "HTTP Protocol",
      value: toDrawerFieldValue(drawerData.requestDetails.httpProtocol),
    },
    {
      key: "requestBody",
      label: "Request Body",
      value: toDrawerFieldValue(drawerData.requestDetails.requestBody),
      monospace: true,
    },
  ].filter(Boolean);

  const legacyRequestFields = eventType === "ratelimit"
    ? rateLimitRequestFields
    : eventType === "bot"
      ? botLegacyRequestFields
      : [];

  const legacyResponseFields = isLegacyEngineDrawer ? [
    {
      key: "statusCode",
      label: "Status Code",
      variant: "status",
      value: eventData.res_status || eventData.resStatus || eventData.res_code,
    },
    (eventData.res_size || eventData.resSize)
      ? {
        key: "responseSize",
        label: "Response Size",
        value: `${(eventData.res_size || eventData.resSize || 0).toLocaleString()} bytes`,
      }
      : null,
    eventType === "ratelimit"
      ? {
        key: "rateLimitStatus",
        label: "Status",
        value: statusString === "Rate Limited" ? "Request was rate limited" : "Request was allowed",
        valueColor: statusString === "Rate Limited" ? "#7C3AED" : "#065F46",
      }
      : null,
    !(event.response_headers && Object.keys(event.response_headers).length > 0)
      && drawerData.responseDetails.responseHeaders.length === 0
      ? { key: "responseHeaders", label: "Response Headers", value: null }
      : null,
  ].filter(Boolean) : [];

  const legacyClientFields = isLegacyEngineDrawer ? [
    {
      key: "sourceIp",
      label: "Source IP",
      value: toDrawerFieldValue(resolveDrawerField(drawerData.networkInformation.sourceIp, sourceIp, event.ip)),
      copyable: true,
    },
    {
      key: "sourcePort",
      label: "Source Port",
      value: toDrawerFieldValue(drawerData.networkInformation.sourcePort),
    },
    {
      key: "ja3Fingerprint",
      label: "JA3 Fingerprint",
      value: toDrawerFieldValue(
        resolveDrawerField(
          drawerData.networkInformation.ja3Fingerprint,
          event.ja3_hash,
          event.ja3
        )
      ),
      monospace: true,
      copyable: true,
    },
    {
      key: "country",
      label: "Country",
      value: toDrawerFieldValue(
        resolveDrawerField(
          drawerData.networkInformation.country,
          event.country_name,
          event.country_iso_code,
          event.country
        )
      ),
    },
    {
      key: "asn",
      label: "ASN",
      value: toDrawerFieldValue(resolveDrawerField(drawerData.networkInformation.asn, event.asn)),
    },
    eventType === "ratelimit" && resolveDrawerField(drawerData.networkInformation.asnOrg, event.asn_org)
      ? {
        key: "asnOrg",
        label: "ASN Organization",
        value: toDrawerFieldValue(resolveDrawerField(drawerData.networkInformation.asnOrg, event.asn_org)),
      }
      : null,
    {
      key: "city",
      label: "City",
      value: toDrawerFieldValue(resolveDrawerField(drawerData.networkInformation.city, event.city)),
    },
    {
      key: "region",
      label: "Region",
      value: toDrawerFieldValue(resolveDrawerField(drawerData.networkInformation.region, event.region)),
    },
    {
      key: "timezone",
      label: "Timezone",
      value: toDrawerFieldValue(resolveDrawerField(drawerData.networkInformation.timezone, event.timezone)),
    },
    eventType === "ratelimit" && resolveDrawerField(event.uid)
      ? {
        key: "uid",
        label: "UID",
        value: toDrawerFieldValue(event.uid),
        monospace: true,
        copyable: true,
      }
      : null,
    eventType === "ratelimit" && resolveDrawerField(event.fp)
      ? {
        key: "fp",
        label: "Fingerprint",
        value: toDrawerFieldValue(event.fp),
        monospace: true,
        copyable: true,
      }
      : null,
    eventType === "ratelimit" && event.postal_code
      ? {
        key: "postalCode",
        label: "Postal Code",
        value: toDrawerFieldValue(event.postal_code),
      }
      : null,
    eventType === "ratelimit" && event.continent_code
      ? {
        key: "continent",
        label: "Continent",
        value: toDrawerFieldValue(event.continent_code),
      }
      : null,
    eventType === "ratelimit" && (event.latitude || event.longitude)
      ? {
        key: "coordinates",
        label: "Coordinates",
        value: [event.latitude, event.longitude].filter(Boolean).join(", ") || null,
        monospace: true,
      }
      : null,
  ].filter(Boolean) : [];

  const standardRequestFields = !isLegacyEngineDrawer && eventType !== "firewall" ? [
    { key: "host", label: "Host", value: drawerData.requestDetails.host },
    {
      key: "uri",
      label: "Requested URI",
      value: drawerData.requestDetails.requestedUri,
      monospace: true,
    },
    { key: "method", label: "Method", value: eventData.method },
    {
      key: "path",
      label: "Path",
      value: legacyRequestPath(eventData),
      monospace: true,
    },
    ...(isSmartParseEvent
      ? [
        { key: "protocol", label: "Protocol", value: drawerData.requestDetails.httpProtocol },
        {
          key: "sourceIp",
          label: "Source IP",
          value: drawerData.networkInformation.sourceIp,
          copyable: true,
        },
        { key: "sourcePort", label: "Source Port", value: drawerData.networkInformation.sourcePort },
        {
          key: "requestId",
          label: "Request ID",
          value: eventData.request_id || eventData.id,
          copyable: true,
        },
        {
          key: "ja3Fp",
          label: "JA3 Fingerprint",
          value: drawerData.requestDetails.ja3Hash,
          copyable: true,
          monospace: true,
        },
        {
          key: "userAgent",
          label: "User Agent",
          value: displayUserAgent(drawerData.requestDetails.userAgent),
        },
      ]
      : []),
    ...(eventType === "ratelimit"
      ? [
        { key: "userAgent", label: "User Agent", value: displayUserAgent(event.ua) },
        { key: "httpVersion", label: "HTTP Version", value: event.http_version },
        { key: "referer", label: "Referer", value: event.referer },
        {
          key: "ja3Rl",
          label: "JA3 Fingerprint",
          value: event.ja3_hash,
          copyable: true,
          monospace: true,
        },
        { key: "cookie", label: "Cookie", value: event.cookie, monospace: true },
      ]
      : []),
    {
      key: "httpProtocol",
      label: "HTTP Protocol",
      value: drawerData.requestDetails.httpProtocol,
    },
    {
      key: "ja3Hash",
      label: "JA3 hash",
      value: drawerData.requestDetails.ja3Hash,
      monospace: true,
      copyable: !!drawerData.requestDetails.ja3Hash,
    },
    {
      key: "requestBody",
      label: "Request Body",
      value: drawerData.requestDetails.requestBody,
      monospace: true,
    },
  ].filter(Boolean) : [];

  const standardResponseFields = !isLegacyEngineDrawer && eventType !== "firewall" ? [
    {
      key: "statusCode",
      label: "Status Code",
      variant: "status",
      value: eventData.res_status || eventData.resStatus || eventData.res_code,
    },
    (eventData.res_size || eventData.resSize)
      ? {
        key: "responseSize",
        label: "Response Size",
        value: `${(eventData.res_size || eventData.resSize || 0).toLocaleString()} bytes`,
      }
      : null,
    isSmartParseEvent && eventData.action
      ? { key: "action", label: "Action", value: eventData.action }
      : null,
    eventType === "ratelimit"
      ? {
        key: "rateLimitStatus",
        label: "Status",
        value: statusString === "Rate Limited" ? "Request was rate limited" : "Request was allowed",
        valueColor: statusString === "Rate Limited" ? "#7C3AED" : "#065F46",
      }
      : null,
    isSmartParseEvent && event.reason
      ? { key: "reason", label: "Reason", value: event.reason }
      : null,
    isSmartParseEvent
      ? {
        key: "blockStatus",
        label: "Status",
        value: statusString === "Blocked" ? "Request was blocked" : "Request was allowed",
        valueColor: statusString === "Blocked" ? "#B91C1C" : "#065F46",
      }
      : null,
  ].filter(Boolean) : [];

  const legacyResponseHeaders =
    event.response_headers
    || (drawerData.responseDetails.responseHeaders.length > 0
      ? drawerData.responseDetails.responseHeaders
      : null);
  const legacyHasResponseHeaders = Array.isArray(legacyResponseHeaders)
    ? legacyResponseHeaders.length > 0
    : legacyResponseHeaders && typeof legacyResponseHeaders === "object"
      ? Object.keys(legacyResponseHeaders).length > 0
      : false;

  const legacyRequestSectionVisible = drawerFieldsHaveContent(legacyRequestFields) || !!legacyQueryParamValue;
  const legacyResponseSectionVisible = drawerFieldsHaveContent(legacyResponseFields) || legacyHasResponseHeaders;
  const legacyClientSectionVisible = drawerFieldsHaveContent(legacyClientFields) || !!countryIso || hasAbuseReputationData(event);

  const hasRateLimitRule = eventType === "ratelimit" && !!event.sd_rate_limit_rule;
  const botDetectors = eventType === "bot" ? getBotEventDetectors(event) : [];
  const hasBotDetectionDetails = eventType === "bot" && (
    !!event.bot_type
    || !!event.bot_name
    || event.bot_score !== undefined
    || !!event.requests_count
    || event.challenge_solved !== undefined
    || botDetectors.length > 0
    || !!event.bm_engine_version
    || !!event.aggregator_version
    || !!event.action_reason
    || !!event.action_intended
    || event.ml_prob !== undefined
    || !!event.ml_model_version
    || !!event.fp_verdict
    || event.fp_confidence !== undefined
    || event.shadow_score !== undefined
    || !!event.shadow_type
  );

  const fwType = eventData.type && Array.isArray(eventData.type) && eventData.type.length > 0 ? eventData.type[0] : null;
  const ruleDetail = eventData.rule_details?.[0];
  const sdFwRule = eventData.raw?.sd_firewall_rule;
  const firewallRuleId =
    fwType?.sec_rule_id
    ?? fwType?.ruleId
    ?? ruleDetail?.ruleId
    ?? sdFwRule?.rule_id
    ?? eventData.rule_id;
  const firewallRuleName =
    fwType?.msg
    ?? ruleDetail?.msg
    ?? sdFwRule?.rule_name
    ?? eventData.rule_name
    ?? eventData.reason;
  const firewallWafTypeLabel =
    fwType?.waf_type !== undefined
      ? fwType.waf_type === 3
        ? "Layerd Firewall"
        : `Type ${fwType.waf_type}`
      : eventData.source_table === "layerd-v2-firewall-events" || firewallRuleId
        ? "Layerd Firewall"
        : null;
  const firewallMatchType = eventData.operator || fwType?.operator;
  const hasFirewallRuleContent = eventType === "firewall" && !!(
    firewallRuleId || firewallRuleName || firewallWafTypeLabel || firewallMatchType
  );

  const firewallNetworkFields = eventType === "firewall" ? [
    { key: "sourceIp", label: "Source IP", value: event.src_ip || drawerData.networkInformation.sourceIp },
    event.ja3_hash || drawerData.networkInformation.ja3Fingerprint
      ? {
        key: "ja3",
        label: "JA3 Fingerprint",
        value: event.ja3_hash || drawerData.networkInformation.ja3Fingerprint,
      }
      : null,
  ].filter(Boolean) : [];

  const firewallIpReputationFields = eventType === "firewall" ? [
    { key: "sourceIp", label: "Source IP", value: drawerData.networkInformation.sourceIp },
    { key: "sourcePort", label: "Source Port", value: drawerData.networkInformation.sourcePort },
    { key: "ja3", label: "JA3 Fingerprint", value: drawerData.networkInformation.ja3Fingerprint },
    { key: "country", label: "Country", value: drawerData.networkInformation.country },
    isDrawerValuePresent(drawerData.networkInformation.asn)
      ? { key: "asn", label: "ASN", value: drawerData.networkInformation.asn }
      : null,
    { key: "city", label: "City", value: drawerData.networkInformation.city },
    { key: "region", label: "Region", value: drawerData.networkInformation.region },
    { key: "timezone", label: "Timezone", value: drawerData.networkInformation.timezone },
  ].filter(Boolean) : [];

  const firewallNetworkSectionVisible = eventType === "firewall" && (
    drawerFieldsHaveContent(firewallNetworkFields) || !!displayUserAgent(event.ua)
  );
  const firewallIpReputationSectionVisible = eventType === "firewall" && (
    drawerFieldsHaveContent(firewallIpReputationFields) || hasAbuseReputationData(event)
  );

  const getRateLimitSimilarStatus = () => {
    const action = String(eventData.action || "").trim().toLowerCase();
    const blocked = eventData.blocked_bool || eventData.sd_rate_limited === 1;
    if (action === "block" || blocked) return "Blocked";
    if (action === "challenge") return "Challenge";
    if (action === "log_only") return "Log only";
    return null;
  };

  const similarEventShape = eventType === "ratelimit" ? {
    attacker: {
      source_ip: sourceIp,
      geo: { country_iso: countryIso },
    },
    target: {
      host: eventData.host_name || eventData.host,
      uri: headerPath,
      path: headerPath,
    },
    assessment: {
      action: eventData.action,
      blocked: eventData.blocked_bool,
      sd_rate_limited: eventData.sd_rate_limited,
      resStatus: eventData.resStatus,
    },
    similarStatus: getRateLimitSimilarStatus(),
  } : eventType === "firewall"
    ? buildFirewallSimilarEventShape(eventData, { sourceIp, countryIso, headerPath })
    : {
    attacker: {
      source_ip: sourceIp,
      geo: { country_iso: countryIso },
    },
    target: {
      host: eventData.host_name || eventData.host,
      uri: headerPath,
      path: headerPath,
    },
    attack_types: eventData.attack_types || eventData.type,
    assessment: { action: eventData.action || eventData.waf_action },
  };

  const handleFilterSimilarV3 = (key) => {
    const legacyKeyMap = {
      ip: "ip",
      attack_type: "detection_type",
      hostname: "hostname",
      uri: "uri",
      country: "country",
      status: "status",
      burst: "burst",
    };
    handleFilterSimilar(legacyKeyMap[key] || key);
  };

  return (
    <>
    <WafEventDrawerShell
      open={open}
      onClose={onClose}
      title={eventConfig.title}
      loading={isEventDetailLoading}
      headerVariant="hero"
      headerContent={(
        <SmartParseDrawerHeader
          embedded
          title={eventConfig.title}
          eventId={headerEventId}
          method={headerMethod}
          path={headerPath}
          onCopyEventId={handleCopy}
        />
      )}
    >
      {eventType === "firewall" ? (
        <SectionCard icon={DETECTION_ICONS.detection} title="Detection Summary">
          <Stack spacing={2}>
            {event.summary && (
              <NetworkFieldRow label="Summary" value={event.summary} />
            )}
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: "wrap", gap: 1 }}>
              {event.verdict && (
                <Chip
                  label={event.verdict.charAt(0).toUpperCase() + event.verdict.slice(1)}
                  size="small"
                  sx={{
                    backgroundColor: event.verdict === "attack" ? "#FEE2E2" : "#D1FAE5",
                    color: event.verdict === "attack" ? "#B91C1C" : "#065F46",
                    fontSize: 13,
                    fontWeight: 500,
                    height: 28,
                    borderRadius: "8px",
                    px: 1,
                  }}
                />
              )}
              {event.risk_level && event.risk_level !== "unknown" && (
                <Chip
                  label={getRiskLevelConfig(event.risk_level).label}
                  size="small"
                  sx={{
                    backgroundColor: getRiskLevelConfig(event.risk_level).bgColor,
                    color: getRiskLevelConfig(event.risk_level).color,
                    fontSize: 13,
                    fontWeight: 500,
                    height: 28,
                    borderRadius: "8px",
                    px: 1,
                  }}
                />
              )}
            </Stack>
            <EventAssessGrid cells={firewallAssessCells} onCopy={handleCopy} />
          </Stack>
        </SectionCard>
      ) : isLegacyEngineDrawer ? (
        <SectionCard icon={DETECTION_ICONS.clipboard} title="Assessment">
          <Stack spacing={2}>
            {statusString && (
              <Chip
                label={statusString}
                size="small"
                sx={{
                  backgroundColor: statusConfig.bgColor,
                  color: statusConfig.textColor,
                  fontSize: 13,
                  fontWeight: 500,
                  height: 28,
                  borderRadius: "8px",
                  px: 1,
                  width: "fit-content",
                }}
              />
            )}
            <EventAssessGrid cells={legacyEngineAssessCells} onCopy={handleCopy} />
          </Stack>
        </SectionCard>
      ) : (
        <SectionCard icon={DETECTION_ICONS.detection} title="Detection Summary">
          <>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
                        {isSmartParseEvent && eventData.verdict ? (
                          <Chip
                            label={eventData.verdict.charAt(0).toUpperCase() + eventData.verdict.slice(1)}
                            size="small"
                            sx={{
                              backgroundColor: eventData.verdict === "malicious" ? "#FEE2E2" : "#D1FAE5",
                              color: eventData.verdict === "malicious" ? "#B91C1C" : "#065F46",
                              fontSize: 13,
                              fontWeight: 500,
                              height: 28,
                              borderRadius: "8px",
                              px: 1,
                            }}
                          />
                        ) : statusString && (
                          <Chip
                            label={statusString}
                            size="small"
                            sx={{
                              backgroundColor: statusConfig.bgColor,
                              color: statusConfig.textColor,
                              fontSize: 13,
                              fontWeight: 500,
                              height: 28,
                              borderRadius: "8px",
                              px: 1,
                            }}
                          />
                        )}
                        {isSmartParseEvent && eventData.risk_level && (
                          <Chip
                            label={getRiskLevelConfig(eventData.risk_level).label}
                            size="small"
                            sx={{
                              backgroundColor: getRiskLevelConfig(eventData.risk_level).bgColor,
                              color: getRiskLevelConfig(eventData.risk_level).color,
                              fontSize: 13,
                              fontWeight: 500,
                              height: 28,
                              borderRadius: "8px",
                              px: 1,
                            }}
                          />
                        )}
                        {/* {hasDetectionTypes && (Array.isArray(eventData.type)
                          ? eventData.type.map((type, idx) => (
                              type.tags.map((tag, tIdx) => (
                                <Chip
                                  key={tIdx}
                                  label={tag}
                                  size="small"
                                  sx={{
                                    backgroundColor: "#FEF3C7",
                                    color: "#92400E",
                                    fontSize: 13,
                                    fontWeight: 500,
                                    height: 28,
                                    borderRadius: "8px",
                                    px: 1,
                                  }}
                                />
                              ))
                            ))
                          : eventData.attack_types?.map((attackType, idx) => (
                              <Chip
                                key={idx}
                                label={attackType}
                                size="small"
                                sx={{
                                  backgroundColor: "#FEF3C7",
                                  color: "#92400E",
                                  fontSize: 13,
                                  fontWeight: 500,
                                  height: 28,
                                  borderRadius: "8px",
                                  px: 1,
                                }}
                              />
                            ))
                        )} */}
                        {isSmartParseEvent && categories && categories.length > 0 && categories.map((cat, idx) => (
                          <Chip
                            key={`cat-${idx}`}
                            label={cat.toUpperCase()}
                            size="small"
                            sx={{
                              backgroundColor: "#EEF0FE",
                              color: PRIMARY_COLOR,
                              fontSize: 13,
                              fontWeight: 500,
                              height: 28,
                              borderRadius: "8px",
                              px: 1,
                            }}
                          />
                        ))}
                      </Stack>
                      {isSmartParseEvent && Array.isArray(eventData.cwe_ids) && eventData.cwe_ids.length > 0 && (
                        <Box sx={{ py: 4 }}>
                          <Typography sx={ { fontSize: 13, color: TEXT_SECONDARY, mb: 1 }}>
                            CWE IDs
                          </Typography>
                          <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
                            {eventData.cwe_ids.map((cwe, idx) => (
                              <Chip
                                key={idx}
                                label={cwe}
                                size="small"
                                sx={{
                                  backgroundColor: "#FEF3C7",
                                  color: "#92400E",
                                  fontSize: 12,
                                  fontWeight: 500,
                                  height: 24,
                                  borderRadius: "6px",
                                }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}
                      {isSmartParseEvent && Array.isArray(eventData.mitre_ids) && eventData.mitre_ids.length > 0 && (
                        <Box sx={{ py: 0.5 }}>
                          <Typography sx={ { fontSize: 13, color: TEXT_SECONDARY, mb: 1 }}>
                            MITRE ATT&CK
                          </Typography>
                          <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
                            {eventData.mitre_ids.map((mitre, idx) => (
                              <Chip
                                key={idx}
                                label={mitre}
                                size="small"
                                sx={{
                                  backgroundColor: "#EEF0FE",
                                  color: PRIMARY_COLOR,
                                  fontSize: 12,
                                  fontWeight: 500,
                                  height: 24,
                                  borderRadius: "6px",
                                }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}
                      {isSmartParseEvent && eventData.summary && (
                        <Box sx={{ py: 1, mt: 3, mb: 2, px: 1.5, backgroundColor: "#F8F9FF", borderRadius: "8px", border: "1px solid #E5E7FF" }}>
                          <Typography sx={ { fontSize: 13, color: "#4B5563", lineHeight: "20px" }}>
                            {eventData.summary}
                          </Typography>
                        </Box>
                      )}
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={3}
                        alignItems="flex-start"
                        sx={{ width: "100%" }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0, width: { xs: "100%", sm: "auto" } }}>
                          <EventAssessGrid
                            cells={[
                              {
                                key: "timestamp",
                                label: "Timestamp",
                                value: eventData.timestamp ? formatDate(eventData.timestamp) : null,
                                icon: Clock,
                              },
                              {
                                key: "hostname",
                                label: "Hostname",
                                value: eventData.host_name || eventData.host,
                                icon: Globe,
                                valueColor: PRIMARY_COLOR,
                              },
                              {
                                key: "verdict",
                                label: "Verdict",
                                value: drawerData.detectionSummary.verdict,
                                icon: ScanSearch,
                                valueColor: assessVerdictValueColor(drawerData.detectionSummary.verdict),
                              },
                              {
                                key: "riskLevel",
                                label: "Risk Level",
                                value: drawerData.detectionSummary.riskLevel,
                                icon: Flame,
                                valueColor: assessRiskLevelValueColor(drawerData.detectionSummary.riskLevel),
                              },
                              ...(isSmartParseEvent && eventData.confidence !== undefined
                                ? []
                                : [{
                                  key: "confidence",
                                  label: "Confidence",
                                  value: drawerData.detectionSummary.confidence,
                                  icon: Activity,
                                }]),
                              {
                                key: "action",
                                label: "Action",
                                value: drawerData.detectionSummary.action,
                                icon: Shield,
                                valueColor: isBlockedAction(drawerData.detectionSummary.action)
                                  ? BLOCKED_STATUS_FG
                                  : TEXT_PRIMARY,
                              },
                              {
                                key: "wafType",
                                label: "WAF Type",
                                value: drawerData.detectionSummary.wafType,
                                icon: DETECTION_ICONS.wafDetection,
                              },
                              {
                                key: "detectedIntents",
                                label: "Detected Intents",
                                value: drawerData.detectionSummary.detectedIntents.length > 0
                                  ? drawerData.detectionSummary.detectedIntents.join(", ")
                                  : null,
                                icon: ScanSearch,
                              },
                              ...(eventData.rule_id
                                ? [{
                                  key: "ruleId",
                                  label: "Rule ID",
                                  value: eventData.rule_id,
                                  icon: Hash,
                                  monospace: true,
                                  copyable: true,
                                }]
                                : []),
                              ...(eventData.rule_name
                                ? [{
                                  key: "ruleName",
                                  label: "Rule Name",
                                  value: eventData.rule_name,
                                  icon: Target,
                                }]
                                : []),
                            ]}
                            onCopy={handleCopy}
                          />
                        </Box>
                        {isSmartParseEvent && eventData.confidence !== undefined && (
                          <Stack
                            spacing={0.5}
                            sx={{
                              flexShrink: 0,
                              alignItems: "center",
                              alignSelf: { xs: "center", sm: "flex-start" },
                              width: { xs: "100%", sm: 190 },
                            }}
                          >
                            <Typography sx={ { fontSize: 13, color: TEXT_SECONDARY }}>
                              Confidence
                            </Typography>
                            <ConfidenceGauge value={eventData.confidence} />
                          </Stack>
                        )}
                      </Stack>
          </>
        </SectionCard>
      )}

                {(!isLegacyEngineDrawer || legacyRequestSectionVisible) && (
                <SectionCard icon={DETECTION_ICONS.request} title="Request Details">
                  {isLegacyEngineDrawer ? (
                    <>
                      <DrawerFieldStack columns={2} onCopy={handleCopy} fields={legacyRequestFields} />
                      {legacyQueryParamValue && (
                        <DrawerFieldStack
                          columns={1}
                          onCopy={handleCopy}
                          fields={[{
                            key: "queryParams",
                            label: "Query Parameters",
                            value: legacyQueryParamValue,
                            monospace: true,
                          }]}
                        />
                      )}
                    </>
                  ) : (
                  <Stack spacing={2}>
                    {eventType === "firewall" ? (
                      <>
                        <DrawerFieldStack
                          columns={2}
                          onCopy={handleCopy}
                          fields={[
                            { key: "host", label: "Host", value: drawerData.requestDetails.host },
                            { key: "uri", label: "Requested URI", value: drawerData.requestDetails.requestedUri },
                            { key: "method", label: "Method", value: event.method || "—" },
                            { key: "path", label: "Path", value: event.uri || "—" },
                            { key: "httpProtocol", label: "HTTP Protocol", value: drawerData.requestDetails.httpProtocol },
                            { key: "ja3Hash", label: "JA3 hash", value: drawerData.requestDetails.ja3Hash, monospace: true },
                            { key: "requestBody", label: "Request Body", value: drawerData.requestDetails.requestBody },
                          ]}
                        />
                        <DrawerHeadersPanel
                          title="Request Headers"
                          headers={event.request_headers}
                          onCopy={handleCopy}
                        />
                      </>
                    ) : (
                      <>
                        <DrawerFieldStack columns={2} onCopy={handleCopy} fields={standardRequestFields} />
                        {isSmartParseEvent && drawerData.requestDetails.requestHeaders.length > 0 && (
                          <Box>
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              onClick={() => setRequestHeadersExpanded((prev) => !prev)}
                              sx={{ cursor: "pointer", mb: 1 }}
                            >
                              <Typography sx={ { fontSize: 13, color: TEXT_SECONDARY }}>
                                Request Headers ({drawerData.requestDetails.requestHeaders.length})
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: 14,
                                  color: TEXT_SECONDARY,
                                  transform: requestHeadersExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                                  transition: "transform 0.2s",
                                }}
                              >
                                ▾
                              </Typography>
                            </Stack>
                            {requestHeadersExpanded && (
                              <Box
                                sx={{
                                  backgroundColor: "#fff",
                                  borderRadius: "8px",
                                  border: `1px solid ${BORDER_COLOR}`,
                                  overflow: "hidden",
                                }}
                              >
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={ { fontSize: 12, fontWeight: 500, color: TEXT_SECONDARY, py: 1, width: "34%", backgroundColor: "#fff", borderBottom: `1px solid ${BORDER_COLOR}` }}>
                                        Header
                                      </TableCell>
                                      <TableCell sx={ { fontSize: 12, fontWeight: 500, color: TEXT_SECONDARY, py: 1, backgroundColor: "#fff", borderBottom: `1px solid ${BORDER_COLOR}` }}>
                                        Value
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {drawerData.requestDetails.requestHeaders.map((item, idx, arr) => (
                                      <TableRow key={`${item.key}_${idx}`} sx={{ "&:hover": { backgroundColor: "#FAFAFA" } }}>
                                        <TableCell sx={ { fontSize: 12, color: TEXT_PRIMARY, py: 1, borderBottom: idx === arr.length - 1 ? "none" : `1px solid ${BORDER_COLOR}` }}>
                                          {item.key}
                                        </TableCell>
                                        <TableCell sx={ { fontSize: 12, color: PRIMARY_COLOR, py: 1, wordBreak: "break-all", borderBottom: idx === arr.length - 1 ? "none" : `1px solid ${BORDER_COLOR}` }}>
                                          {item.value}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            )}
                          </Box>
                        )}
                        {eventType === "ratelimit" && Array.isArray(event.query) && event.query.length > 0 && (
                          <Box>
                            <Typography sx={ { fontSize: 13, color: TEXT_SECONDARY, mb: 0.5 }}>
                              Query Parameters
                            </Typography>
                            <Stack spacing={0.5}>
                              {event.query.map((param, idx) => (
                                <Box key={idx}>
                                  <Typography component="span" sx={ { fontSize: 13, color: TEXT_SECONDARY }}>
                                    {param.key}=
                                  </Typography>
                                  <Typography component="span" sx={ { fontSize: 13, color: PRIMARY_COLOR }}>
                                    {param.value}
                                  </Typography>
                                </Box>
                              ))}
                            </Stack>
                          </Box>
                        )}
                        {eventType !== "ratelimit" && eventData.query_string && (
                          <Box>
                            <Typography sx={{ ...baseFontStyle, fontSize: 12, color: TEXT_SECONDARY, mb: 0.5 }}>
                              Query String
                            </Typography>
                            <Box sx={{
                              backgroundColor: "#fff",
                              borderRadius: "8px",
                              p: 1.5,
                              border: `1px solid ${BORDER_COLOR}`,
                            }}>
                              <Typography sx={{ fontFamily: "monospace", fontSize: 14, color: PRIMARY_COLOR, wordBreak: "break-all" }}>
                                {eventData.query_string}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </>
                    )}
                  </Stack>
                  )}
                </SectionCard>
                )}

                {(!isLegacyEngineDrawer || legacyResponseSectionVisible) && (
                <SectionCard icon={DETECTION_ICONS.response} title="Response Details">
                  {isLegacyEngineDrawer ? (
                    <>
                      <DrawerFieldStack columns={2} onCopy={handleCopy} fields={legacyResponseFields} />
                      <DrawerHeadersPanel
                        title="Response Headers"
                        headers={legacyResponseHeaders}
                        onCopy={handleCopy}
                      />
                    </>
                  ) : (
                  <Stack spacing={2}>
                    {eventType === "firewall" ? (
                      <>
                        <DrawerFieldStack
                          columns={2}
                          fields={[
                            {
                              key: "status",
                              label: "Status Code",
                              variant: "status",
                              value: event.res_status ?? event.resStatus,
                            },
                            ...(drawerData.responseDetails.responseHeaders.length > 0
                              || (event.response_headers && Object.keys(event.response_headers).length > 0)
                              ? []
                              : [{ key: "responseHeaders", label: "Response Headers", value: "—" }]),
                          ]}
                        />
                        {(drawerData.responseDetails.responseHeaders.length > 0
                          || (event.response_headers && Object.keys(event.response_headers).length > 0)) && (
                          <DrawerHeadersPanel
                            title="Response Headers"
                            headers={event.response_headers}
                            onCopy={handleCopy}
                          />
                        )}
                      </>
                    ) : (
                      <>
                        <DrawerFieldStack columns={2} onCopy={handleCopy} fields={standardResponseFields} />
                      </>
                    )}
                    {eventType !== "firewall" && (
                    <Box>
                      <Typography sx={ { fontSize: 13, color: TEXT_SECONDARY, mb: 1 }}>
                        Response Headers
                      </Typography>
                      {drawerData.responseDetails.responseHeaders.length > 0 ? (
                        <Box sx={{ backgroundColor: "#fff", borderRadius: "8px", border: `1px solid ${BORDER_COLOR}`, overflow: "hidden" }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={ { fontSize: 12, color: TEXT_SECONDARY, fontWeight: 500, py: 1, width: "34%" }}>
                                  Header
                                </TableCell>
                                <TableCell sx={ { fontSize: 12, color: TEXT_SECONDARY, fontWeight: 500, py: 1 }}>
                                  Value
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {drawerData.responseDetails.responseHeaders.map((item, idx, arr) => (
                                <TableRow key={`${item.key}_${idx}`}>
                                  <TableCell sx={ { fontSize: 12, color: TEXT_PRIMARY, py: 1, borderBottom: idx === arr.length - 1 ? "none" : `1px solid ${BORDER_COLOR}` }}>
                                    {item.key}
                                  </TableCell>
                                  <TableCell sx={ { fontSize: 12, color: PRIMARY_COLOR, py: 1, wordBreak: "break-all", borderBottom: idx === arr.length - 1 ? "none" : `1px solid ${BORDER_COLOR}` }}>
                                    {item.value}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      ) : (
                        <Typography sx={ { fontSize: 12, color: TEXT_PRIMARY, }}>—</Typography>
                      )}
                    </Box>
                    )}
                  </Stack>
                  )}
                </SectionCard>
                )}

                {eventType !== "firewall" && (!isLegacyEngineDrawer || legacyClientSectionVisible) && (
                <SectionCard icon={DETECTION_ICONS.ipReputation} title={isSmartParseEvent ? "Network Information" : (eventType === "ratelimit" ? "Client Information" : "IP Reputation")}>
                  {isLegacyEngineDrawer ? (
                    <>
                      {countryIso && (
                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
                          <ReactCountryFlag
                            countryCode={countryIso}
                            svg
                            style={{
                              width: "32px",
                              height: "22px",
                              borderRadius: "4px",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                            }}
                          />
                          <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>
                            {toDrawerFieldValue(countryName) || countryIso}
                          </Typography>
                        </Stack>
                      )}
                      <DrawerFieldStack columns={2} onCopy={handleCopy} fields={legacyClientFields} />
                      {hasAbuseReputationData(event) && (
                        <AbuseConfidenceScoreCard
                          reputation={{
                            abuse_confidence_score: event.abuseip?.abuseConfidenceScore ?? event.abuse_score ?? event.ip_reputation_score,
                            isp: event.abuseip?.isp,
                            usage_type: event.abuseip?.usageType,
                            country_code: event.abuseip?.countryCode,
                            total_reports: event.abuseip?.totalReports,
                          }}
                          countryFallback={countryIso}
                        />
                      )}
                    </>
                  ) : (
                  <Stack spacing={2}>
                    <NetworkInformationPanel
                      network={{
                        ...drawerData.networkInformation,
                        countryCode: countryIso,
                      }}
                      onCopy={handleCopy}
                    />
                    {eventType === "ratelimit" && (event.country_name || event.city) && (
                      <DrawerFieldStack
                        columns={2}
                        fields={[
                          { key: "country", label: "Country", value: event.country_name },
                          { key: "city", label: "City", value: event.city },
                          { key: "region", label: "Region", value: event.region },
                          { key: "postalCode", label: "Postal Code", value: event.postal_code },
                          (event.timezone || event.continent_code)
                            ? { key: "timezone", label: "Timezone", value: event.timezone }
                            : null,
                          (event.timezone || event.continent_code)
                            ? { key: "continent", label: "Continent", value: event.continent_code }
                            : null,
                          (event.latitude || event.longitude)
                            ? { key: "latitude", label: "Latitude", value: event.latitude?.toString() }
                            : null,
                          (event.latitude || event.longitude)
                            ? { key: "longitude", label: "Longitude", value: event.longitude?.toString() }
                            : null,
                          (event.asn || event.ja3_hash)
                            ? { key: "asn", label: "ASN", value: event.asn }
                            : null,
                          (event.asn || event.ja3_hash)
                            ? { key: "ja3Hash", label: "JA3 Hash", value: event.ja3_hash, monospace: true }
                            : null,
                        ]}
                      />
                    )}
                    {!isSmartParseEvent && eventType !== "ratelimit" && hasAbuseReputationData(event) && (
                      <IpReputationPanel
                        ipReputation={{
                          abuseConfidenceScore: event.abuseip?.abuseConfidenceScore ?? event.abuse_score ?? event.ip_reputation_score,
                          isp: event.abuseip?.isp,
                          usageType: event.abuseip?.usageType,
                          countryCode: event.abuseip?.countryCode,
                          totalReports: event.abuseip?.totalReports,
                          ipAddress: sourceIp,
                        }}
                      />
                    )}
                  </Stack>
                  )}
                </SectionCard>
                )}

                {isSmartParseEvent && campaignDrawer && (
                  <SectionCard icon={DETECTION_ICONS.campaign} title="Campaign">
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
                        {campaignDrawer.isEscalating && (
                          <Chip
                            label="Escalating"
                            size="small"
                            sx={{
                              fontSize: 11,
                              height: 24,
                              borderRadius: "8px",
                              backgroundColor: "#FEE2E2",
                              color: "#B91C1C",
                              fontWeight: 600,
                            }}
                          />
                        )}
                        {campaignDrawer.isMultiVector && (
                          <Chip
                            label="Multi-vector"
                            size="small"
                            sx={{
                              fontSize: 11,
                              height: 24,
                              borderRadius: "8px",
                              backgroundColor: "#EDE9FE",
                              color: "#5B21B6",
                              fontWeight: 600,
                            }}
                          />
                        )}
                        {campaignDrawer.uniqueIntentTypes.map((intentId) => (
                          <Chip
                            key={intentId}
                            label={intentId}
                            size="small"
                            sx={{
                              fontFamily: "Rubik, monospace",
                              fontSize: 11,
                              height: 24,
                              borderRadius: "8px",
                              backgroundColor: "#EEF0FE",
                              color: PRIMARY_COLOR,
                            }}
                          />
                        ))}
                      </Stack>

                      <DrawerFieldStack
                        columns={2}
                        fields={[
                          campaignDrawer.campaignAgeLabel
                            ? { key: "age", label: "Campaign age", value: campaignDrawer.campaignAgeLabel }
                            : null,
                          campaignDrawer.detectionCount != null
                            ? { key: "count", label: "Detection count", value: String(campaignDrawer.detectionCount) }
                            : null,
                          campaignDrawer.progression !== ""
                            ? { key: "progression", label: "Progression", value: campaignDrawer.progression }
                            : null,
                        ]}
                      />

                      {campaignDrawer.killChain.length > 0 && (
                        <Box>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 500,
                              color: TEXT_PRIMARY,
                              mb: 1,
                            }}
                          >
                            Kill chain
                          </Typography>
                          <Stack spacing={1.5}>
                            {campaignDrawer.killChain.map((step, ki) => {
                              const verdictCfg = getRiskLevelConfig(step.verdict);
                              return (
                                <Box
                                  key={`kill-${ki}`}
                                  sx={{
                                    pl: 2,
                                    borderLeft: `3px solid ${PRIMARY_COLOR}`,
                                    backgroundColor: "#FAFAFA",
                                    borderRadius: "0 8px 8px 0",
                                    py: 1.5,
                                    pr: 1.5,
                                  }}
                                >
                                  <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ gap: 1, mb: 0.75 }}>
                                    {step.stage && (
                                      <Chip
                                        label={step.stage}
                                        size="small"
                                        sx={{
                                          fontSize: 11,
                                          height: 22,
                                          borderRadius: "6px",
                                          backgroundColor: "#E0E7FF",
                                          color: "#3730A3",
                                          textTransform: "capitalize",
                                        }}
                                      />
                                    )}
                                    {step.verdict && (
                                      <Chip
                                        label={verdictCfg.label}
                                        size="small"
                                        sx={{
                                          fontSize: 11,
                                          height: 22,
                                          borderRadius: "6px",
                                          backgroundColor: verdictCfg.bgColor,
                                          color: verdictCfg.color,
                                        }}
                                      />
                                    )}
                                    {step.confidencePct != null && (
                                      <Chip
                                        label={`${step.confidencePct}% confidence`}
                                        size="small"
                                        sx={{
                                          fontSize: 11,
                                          height: 22,
                                          borderRadius: "6px",
                                          backgroundColor: "#F3F4F6",
                                          color: TEXT_SECONDARY,
                                        }}
                                      />
                                    )}
                                  </Stack>
                                  {step.intent && (
                                    <Typography
                                      sx={{
                                        fontFamily: "Rubik, monospace",
                                        fontSize: 12,
                                        color: TEXT_PRIMARY,
                                        fontWeight: 500,
                                        mb: 0.5,
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {step.intent}
                                    </Typography>
                                  )}
                                  {step.timestamp && (
                                    <Typography sx={ { fontSize: 12, color: TEXT_SECONDARY, mb: step.payloadPreview ? 0.75 : 0 }}>
                                      {formatDate(step.timestamp)}
                                    </Typography>
                                  )}
                                  {step.payloadPreview && (
                                    <Typography
                                      component="div"
                                      sx={{
                                        fontFamily: "monospace",
                                        fontSize: 11,
                                        color: PRIMARY_COLOR,
                                        wordBreak: "break-all",
                                        lineHeight: "25px",
                                        backgroundColor: "#fff",
                                        border: `1px solid ${BORDER_COLOR}`,
                                        borderRadius: "6px",
                                        px: 1,
                                        py: 0.75,
                                      }}
                                    >
                                      {step.payloadPreview}
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })}
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                  </SectionCard>
                )}

                {hasRateLimitRule && (
                  <SectionCard icon={DETECTION_ICONS.match} title="Triggered Rate Limit Rule">
                    {(() => {
                      const rlRule = event.sd_rate_limit_rule || {};

                      return (
                        <Stack spacing={2.5}>
                          <DrawerFieldStack
                            columns={2}
                            onCopy={handleCopy}
                            fields={[
                              rlRule.requests_per_period !== undefined
                                ? { key: "requests", label: "Requests", value: `${rlRule.requests_per_period}` }
                                : null,
                              rlRule.period !== undefined
                                ? { key: "period", label: "Period", value: `${rlRule.period}s` }
                                : null,
                              rlRule.mitigation_timeout !== undefined
                                ? { key: "timeout", label: "Timeout", value: `${rlRule.mitigation_timeout}s` }
                                : null,
                              rlRule.rule_name
                                ? { key: "ruleName", label: "Rule Name", value: rlRule.rule_name }
                                : null,
                              rlRule.rule_id
                                ? {
                                  key: "ruleId",
                                  label: "Rule ID",
                                  value: rlRule.rule_id,
                                  copyable: true,
                                  monospace: true,
                                }
                                : null,
                              rlRule.host_name
                                ? { key: "hostName", label: "Host Name", value: rlRule.host_name }
                                : null,
                              rlRule.characteristics && rlRule.characteristics.length > 0
                                ? {
                                  key: "characteristics",
                                  label: "Characteristics",
                                  value: rlRule.characteristics.join(", "),
                                }
                                : null,
                              rlRule.action
                                ? {
                                  key: "action",
                                  label: "Action",
                                  variant: "status",
                                  value: rlRule.action,
                                }
                                : null,
                            ]}
                          />

                          {rlRule.conditions && rlRule.conditions.length > 0 && (
                            <Box>
                              <Typography sx={ { fontSize: 13, fontWeight: 500, color: TEXT_PRIMARY, mb: 1.5 }}>
                                Conditions
                              </Typography>
                              <Stack spacing={1.5}>
                                {(() => {
                                  const flatConditions = rlRule.conditions.flat();
                                  return flatConditions.map((condition, idx) => (
                                    <Box key={idx} sx={{
                                      backgroundColor: "#fff",
                                      borderRadius: "8px",
                                      border: `1px solid ${BORDER_COLOR}`,
                                      p: 2,
                                    }}>
                                      <DrawerFieldStack
                                        columns={2}
                                        onCopy={handleCopy}
                                        fields={[
                                          { key: "field", label: "Field", value: condition.key || condition.field },
                                          { key: "operator", label: "Operator", value: condition.condition || condition.operator },
                                          { key: "value", label: "Value", value: condition.value },
                                        ]}
                                      />
                                    </Box>
                                  ));
                                })()}
                              </Stack>
                            </Box>
                          )}
                        </Stack>
                      );
                    })()}
                  </SectionCard>
                )}

                {hasFirewallRuleContent && (
                  <SectionCard icon={DETECTION_ICONS.match} title="Triggered Firewall Rule">
                    <DrawerFieldStack
                      onCopy={handleCopy}
                      fields={[
                        firewallRuleId
                          ? {
                            key: "ruleId",
                            label: "Rule ID",
                            value: String(firewallRuleId),
                            monospace: true,
                            copyable: true,
                          }
                          : null,
                        firewallRuleName ? { key: "ruleName", label: "Rule Name", value: firewallRuleName } : null,
                        firewallWafTypeLabel ? { key: "wafType", label: "WAF Type", value: firewallWafTypeLabel } : null,
                        firewallMatchType ? { key: "matchType", label: "Match Type", value: firewallMatchType } : null,
                      ]}
                    />
                  </SectionCard>
                )}

                {firewallNetworkSectionVisible && (
                  <SectionCard icon={DETECTION_ICONS.ipReputation} title="Network Information">
                    <Stack spacing={2.5}>
                      <DrawerFieldStack
                        onCopy={handleCopy}
                        fields={firewallNetworkFields.map((field) => (
                          field.key === "ja3"
                            ? { ...field, copyable: true, monospace: true }
                            : field
                        ))}
                      />
                      {displayUserAgent(event.ua) && (
                        <RequestHeadersSection
                          userAgent={displayUserAgent(event.ua)}
                          onCopy={handleCopy}
                        />
                      )}
                    </Stack>
                  </SectionCard>
                )}

                {firewallIpReputationSectionVisible && (
                  <SectionCard icon={DETECTION_ICONS.ipReputation} title="IP Reputation">
                    <Stack spacing={2.5}>
                      <DrawerFieldStack
                        columns={2}
                        onCopy={handleCopy}
                        fields={firewallIpReputationFields}
                      />
                      {hasAbuseReputationData(event) && (
                        <IpReputationPanel
                          ipReputation={{
                            abuseConfidenceScore: event.abuseip?.abuseConfidenceScore,
                            isp: event.abuseip?.isp,
                            usageType: event.abuseip?.usageType,
                            countryCode: event.abuseip?.countryCode,
                            totalReports: event.abuseip?.totalReports,
                          }}
                        />
                      )}
                    </Stack>
                  </SectionCard>
                )}

                {hasBotDetectionDetails && (
                  <SectionCard icon={DETECTION_ICONS.detection} title="Bot Detection Details">
                    <DrawerFieldStack
                      columns={2}
                      fields={[
                        event.bot_type ? { key: "botType", label: "Bot Type", value: event.bot_type } : null,
                        event.bot_name ? { key: "botName", label: "Bot Name", value: event.bot_name } : null,
                        event.requests_count
                          ? { key: "reqCount", label: "Request Count", value: event.requests_count.toLocaleString() }
                          : null,
                        event.challenge_solved !== undefined
                          ? { key: "challenge", label: "Challenge Solved", value: event.challenge_solved ? "Yes" : "No" }
                          : null,
                      ]}
                    />
                    {event.bot_score !== undefined && (
                      <Box sx={{ py: 2 }}>
                        <Typography sx={{ ...baseFontStyle, color: TEXT_SECONDARY, mb: 1 }}>
                          Bot Score
                        </Typography>
                        <Typography sx={{
                          fontSize: 48,
                          fontWeight: 600,
                          lineHeight: 1,
                          color: PRIMARY_COLOR
                        }}>
                          {event.bot_score}
                        </Typography>
                        <Typography sx={{ ...baseFontStyle, color: PRIMARY_COLOR, mt: 1 }}>
                          {event.bot_score >= 1 && event.bot_score <= 20 && "Bad Bot"}
                          {event.bot_score >= 21 && event.bot_score <= 30 && "Good Bot (Verified)"}
                          {event.bot_score >= 31 && event.bot_score <= 100 && "Human"}
                        </Typography>
                      </Box>
                    )}
                    {botDetectors.length > 0 && (
                      <Box sx={{ py: 2 }}>
                        <Typography sx={{ ...baseFontStyle, color: TEXT_SECONDARY, mb: 1 }}>
                          Fired Detectors ({botDetectors.length})
                        </Typography>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                          {botDetectors.map((det) => (
                            <Chip
                              key={det}
                              size="small"
                              label={det}
                              sx={{
                                height: 22,
                                fontSize: 12,
                                borderRadius: "11px",
                                background: "#EEF2FF",
                                color: "#3730A3",
                                border: "1px solid #C7D2FE",
                              }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    )}
                    <DrawerFieldStack
                      columns={2}
                      fields={[
                        event.action_reason ? { key: "actionReason", label: "Action Reason", value: event.action_reason } : null,
                        event.action_intended && event.action_intended !== event.action
                          ? { key: "actionIntended", label: "Action Intended", value: event.action_intended }
                          : null,
                        event.ml_prob !== undefined && event.ml_prob !== 0
                          ? { key: "mlProb", label: "ML Probability", value: Number(event.ml_prob).toFixed(3) }
                          : null,
                        event.ml_model_version ? { key: "mlModel", label: "ML Model", value: event.ml_model_version } : null,
                        event.fp_verdict ? { key: "fpVerdict", label: "FP Verdict", value: event.fp_verdict } : null,
                        event.fp_confidence !== undefined && event.fp_confidence !== 0
                          ? { key: "fpConf", label: "FP Confidence", value: Number(event.fp_confidence).toFixed(3) }
                          : null,
                        event.shadow_score !== undefined && event.shadow_score !== 0
                          ? { key: "shadowScore", label: "Shadow Score", value: event.shadow_score }
                          : null,
                        event.shadow_type ? { key: "shadowType", label: "Shadow Type", value: event.shadow_type } : null,
                        event.bm_engine_version ? { key: "bmEngineVer", label: "BM Engine Version", value: event.bm_engine_version } : null,
                        event.aggregator_version ? { key: "aggVer", label: "Aggregator Version", value: event.aggregator_version } : null,
                      ]}
                    />
                  </SectionCard>
                )}

                {isSmartParseEvent && attackDisplayList && attackDisplayList.length > 0 && (
                  <SectionCard icon={DETECTION_ICONS.detection} title={`Attacks Detected (${attackDisplayList.length})`}>
                    <Stack direction="row" flexWrap="wrap" gap={1.5}>
                      {attackDisplayList.map((attack, idx) => {
                        const isStringAttack = typeof attack === "string";
                        const attackType = isStringAttack ? attack : (attack.type || attack.attack_type);
                        const attackSeverity = isStringAttack ? null : (attack.severity || attack.risk_level);
                        if (isStringAttack) {
                          return (
                            <Box
                              key={idx}
                              sx={{
                                px: 1.5,
                                py: 1,
                                backgroundColor: "#EEF2FF",
                                borderRadius: "8px",
                                border: "1px solid #C7D2FE",
                                width: "fit-content",
                                maxWidth: "100%",
                              }}
                            >
                              <Typography
                                sx={{
                                  ...baseFontStyle,
                                  fontWeight: 500,
                                  color: PRIMARY_COLOR,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {attackType}
                              </Typography>
                            </Box>
                          );
                        }
                        return (
                          <Box 
                            key={idx} 
                            sx={{ 
                              p: 2.25, 
                              backgroundColor: "#EEF2FF", 
                              borderRadius: "10px",
                              border: "1px solid #C7D2FE",
                              flex: "1 1 100%",
                            }}
                          >
                            <Stack spacing={1.5}>
                              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                <Box sx={{ flex: 1 }}>
                                  <Typography sx={{ ...baseFontStyle, fontWeight: 500, color: PRIMARY_COLOR }}>
                                    {attackType}
                                  </Typography>
                                  {!isStringAttack && attack.confidence !== undefined && (
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                                      <Typography sx={{ ...baseFontStyle, fontSize: 12, color: TEXT_SECONDARY }}>
                                        Confidence:
                                      </Typography>
                                      <Box sx={{ width: 80, height: 6, backgroundColor: "#F3F4F6", borderRadius: "8px" }}>
                                        <Box sx={{ 
                                          width: `${attack.confidence}%`, 
                                          height: "100%", 
                                          backgroundColor: PRIMARY_COLOR,
                                          borderRadius: "8px",
                                        }} />
                                      </Box>
                                      <Typography sx={{ ...baseFontStyle, fontSize: 12, fontWeight: 600, color: PRIMARY_COLOR }}>
                                        {attack.confidence}%
                                      </Typography>
                                    </Stack>
                                  )}
                                </Box>
                                {attackSeverity && (
                                  <Chip
                                    label={getRiskLevelConfig(attackSeverity).label}
                                    size="small"
                                    sx={{
                                      fontSize: 12,
                                      height: 24,
                                      borderRadius: "8px",
                                      fontWeight: 500,
                                      backgroundColor: getRiskLevelConfig(attackSeverity).bgColor,
                                      color: getRiskLevelConfig(attackSeverity).color,
                                    }}
                                  />
                                )}
                              </Stack>

                              {!isStringAttack && (
                                <Stack direction="row" flexWrap="wrap" gap={1}>
                                  {(attack.mitre || attack.mitre_id) && (
                                    <Chip 
                                      label={`MITRE: ${attack.mitre || attack.mitre_id}`} 
                                      size="small" 
                                      sx={ { fontSize: 12, height: 24, borderRadius: "999px", backgroundColor: "#DBEAFE", color: "#1E40AF" }} 
                                    />
                                  )}
                                  {(attack.cwe || attack.cwe_id) && (
                                    <Chip 
                                      label={attack.cwe || attack.cwe_id} 
                                      size="small" 
                                      sx={ { fontSize: 12, height: 24, borderRadius: "999px", backgroundColor: "#F3E8FF", color: "#7C3AED" }} 
                                    />
                                  )}
                                  {Array.isArray(attack.intent_tags) && attack.intent_tags.length > 0 && attack.intent_tags.map((tag, tIdx) => (
                                    <Chip
                                      key={tIdx}
                                      label={tag}
                                      size="small"
                                      sx={{
                                        fontSize: 11,
                                        height: 22,
                                        borderRadius: "999px",
                                        backgroundColor: "#E0F2FE",
                                        color: "#0369A1",
                                      }}
                                    />
                                  ))}
                                </Stack>
                              )}

                              {!isStringAttack && attack.evidence && Array.isArray(attack.evidence) && attack.evidence.length > 0 && (
                                (() => {
                                  const TOP_EVIDENCE_COUNT = 3;
                                  const evidenceList = Array.isArray(attack.evidence) ? attack.evidence : [];
                                  const normalized = evidenceList.map((ev, evIdx) => {
                                    const { scope, patternText, position, score, positionNum, scoreNum } = normalizeEvidence(ev);
                                    return { scope, patternText, position, score, positionNum, scoreNum, evIdx };
                                  });

                                  const sorted = normalized
                                    .map((item, order) => ({ ...item, order }))
                                    .sort((a, b) => {
                                      // Higher score first; then smaller position.
                                      if (b.scoreNum !== a.scoreNum) return b.scoreNum - a.scoreNum;
                                      if (a.positionNum !== b.positionNum) return a.positionNum - b.positionNum;
                                      return a.order - b.order;
                                    });

                                  const total = sorted.length;
                                  const isExpanded = !!expandedEvidenceMap[idx];
                                  const topEvidence = sorted.slice(0, TOP_EVIDENCE_COUNT);

                                  return (
                                    <Box>
                                      <Typography sx={{ ...baseFontStyle, fontWeight: 500, color: TEXT_PRIMARY, mb: 1.5 }}>
                                        Evidence ({total} matches)
                                      </Typography>

                                      {!isExpanded ? (
                                        <Stack spacing={1.25}>
                                          <Stack direction="row" flexWrap="wrap" gap={1}>
                                            {topEvidence.map((evItem, cardIdx) => {
                                              const scopeColors = getScopeChipColors(evItem.scope);
                                              const scoreLabel = evItem.score !== undefined && evItem.score !== null ? `+${evItem.score}` : null;
                                              return (
                                                <Box
                                                  key={`${evItem.evIdx}_${cardIdx}`}
                                                  sx={{
                                                    flex: "1 1 240px",
                                                    minWidth: 240,
                                                    backgroundColor: "#fff",
                                                    border: `1px solid ${BORDER_COLOR}`,
                                                    borderRadius: "8px",
                                                    p: 1.5,
                                                  }}
                                                >
                                                  <Stack spacing={1}>
                                                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                                      <Chip
                                                        label={evItem.scope}
                                                        size="small"
                                                        sx={{
                                                          fontSize: 11,
                                                          height: 22,
                                                          borderRadius: "8px",
                                                          backgroundColor: scopeColors.backgroundColor,
                                                          color: scopeColors.color,
                                                        }}
                                                      />
                                                      {scoreLabel && (
                                                        <Chip
                                                          label={scoreLabel}
                                                          size="small"
                                                          sx={{
                                                            fontSize: 11,
                                                            height: 22,
                                                            borderRadius: "8px",
                                                            backgroundColor: "#EEF0FE",
                                                            color: PRIMARY_COLOR,
                                                          }}
                                                        />
                                                      )}
                                                    </Stack>

                                                    <Typography
                                                      sx={{
                                                        fontFamily: "monospace",
                                                        fontSize: 13,
                                                        color: PRIMARY_COLOR,
                                                        wordBreak: "break-all",
                                                        lineHeight: "18px",
                                                      }}
                                                    >
                                                      {evItem.patternText}
                                                    </Typography>

                                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                      <Typography sx={ { fontSize: 11, color: TEXT_SECONDARY, fontWeight: 600 }}>
                                                        Position
                                                      </Typography>
                                                      <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: PRIMARY_COLOR }}>
                                                        {evItem.position !== undefined ? evItem.position : "—"}
                                                      </Typography>
                                                    </Stack>
                                                  </Stack>
                                                </Box>
                                              );
                                            })}
                                          </Stack>

                                          {total > TOP_EVIDENCE_COUNT && (
                                            <Button
                                              variant="outlined"
                                              onClick={() => setExpandedEvidenceMap((prev) => ({ ...prev, [idx]: true }))}
                                              sx={{
                                                ...secondaryButtonStyle,
                                                alignSelf: "flex-start",
                                              }}
                                            >
                                              Show full evidence ({total})
                                            </Button>
                                          )}
                                        </Stack>
                                      ) : (
                                        <Stack spacing={1.25}>
                                          <Box
                                            sx={{
                                              backgroundColor: "#fff",
                                              borderRadius: "8px",
                                              border: `1px solid ${BORDER_COLOR}`,
                                              overflow: "hidden",
                                            }}
                                          >
                                            <Table size="small">
                                              <TableHead>
                                                <TableRow>
                                                  <TableCell sx={{ ...baseFontStyle, fontSize: 12, color: TEXT_SECONDARY, fontWeight: 500, py: 1, width: "20%", backgroundColor: "#fff", borderBottom: `1px solid ${BORDER_COLOR}` }}>
                                                    Scope
                                                  </TableCell>
                                                  <TableCell sx={{ ...baseFontStyle, fontSize: 12, color: TEXT_SECONDARY, fontWeight: 500, py: 1, backgroundColor: "#fff", borderBottom: `1px solid ${BORDER_COLOR}` }}>
                                                    Pattern
                                                  </TableCell>
                                                  <TableCell sx={{ ...baseFontStyle, fontSize: 12, color: TEXT_SECONDARY, fontWeight: 500, py: 1, width: "15%", textAlign: "center", backgroundColor: "#fff", borderBottom: `1px solid ${BORDER_COLOR}` }}>
                                                    Position
                                                  </TableCell>
                                                </TableRow>
                                              </TableHead>
                                              <TableBody>
                                                {sorted.map((evItem, evIdx) => {
                                                  const isLast = evIdx === sorted.length - 1;
                                                  const scopeColors = getScopeChipColors(evItem.scope);
                                                  return (
                                                    <TableRow key={`${evItem.evIdx}_${evIdx}`} sx={{ "&:hover": { backgroundColor: "#FAFAFA" } }}>
                                                      <TableCell sx={{ ...baseFontStyle, fontSize: 12, color: TEXT_PRIMARY, py: 1, borderBottom: isLast ? "none" : `1px solid ${BORDER_COLOR}` }}>
                                                        <Chip
                                                          label={evItem.scope}
                                                          size="small"
                                                          sx={{
                                                            fontSize: 11,
                                                            height: 22,
                                                            borderRadius: "8px",
                                                            backgroundColor: scopeColors.backgroundColor,
                                                            color: scopeColors.color,
                                                          }}
                                                        />
                                                      </TableCell>
                                                      <TableCell sx={{ fontFamily: "monospace", fontSize: 14, color: PRIMARY_COLOR, py: 1, wordBreak: "break-all", borderBottom: isLast ? "none" : `1px solid ${BORDER_COLOR}` }}>
                                                        {evItem.patternText}
                                                      </TableCell>
                                                      <TableCell sx={{ fontFamily: "monospace", fontSize: 14, color: PRIMARY_COLOR, py: 1, textAlign: "center", borderBottom: isLast ? "none" : `1px solid ${BORDER_COLOR}` }}>
                                                        {evItem.position !== undefined ? evItem.position : "—"}
                                                      </TableCell>
                                                    </TableRow>
                                                  );
                                                })}
                                              </TableBody>
                                            </Table>
                                          </Box>

                                          <Button
                                            variant="outlined"
                                            onClick={() => setExpandedEvidenceMap((prev) => ({ ...prev, [idx]: false }))}
                                            sx={{
                                              ...secondaryButtonStyle,
                                              alignSelf: "flex-start",
                                            }}
                                          >
                                            Show less
                                          </Button>
                                        </Stack>
                                      )}
                                    </Box>
                                  );
                                })()
                              )}
                            </Stack>
                          </Box>
                        );
                      })}
                    </Stack>
                  </SectionCard>
                )}

                {intentTagsRich.length > 0 ? (
                  <SectionCard icon={DETECTION_ICONS.info} title={`Intent Tags (${intentTagsRich.length})`}>
                    <Stack spacing={2}>
                      {intentTagsRich.map((tag, idx) => {
                        const severityLabel = (tag.severity_label || tag.severity || "").trim();
                        const metaParts = [tag.id, tag.category]
                          .map((x) => (x != null ? String(x).trim() : ""))
                          .filter(Boolean);
                        return (
                        <Box 
                          key={`intent-tag-${idx}`} 
                          sx={{ 
                            p: 2, 
                            backgroundColor: "#fff", 
                            borderRadius: "8px",
                            border: `1px solid ${BORDER_COLOR}`,
                          }}
                        >
                          <Stack spacing={1.5}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Typography sx={{ ...baseFontStyle, fontWeight: 500, color: TEXT_PRIMARY }}>
                                {tag.name}
                              </Typography>
                              {severityLabel ? (
                              <Chip
                                label={getRiskLevelConfig(severityLabel).label}
                                size="small"
                                sx={{
                                  fontSize: 11,
                                  height: 22,
                                  borderRadius: "8px",
                                  backgroundColor: getRiskLevelConfig(severityLabel).bgColor,
                                  color: getRiskLevelConfig(severityLabel).color,
                                }}
                              />
                              ) : null}
                            </Stack>
                            {metaParts.length > 0 ? (
                            <Typography sx={{ ...baseFontStyle, fontSize: 12, color: TEXT_SECONDARY }}>
                              {metaParts.join(" • ")}
                            </Typography>
                            ) : null}
                            {tag.description && (
                              <Typography sx={{ ...baseFontStyle, fontSize: 12, color: TEXT_PRIMARY }}>
                                {tag.description}
                              </Typography>
                            )}
                            {tag.matched && (
                              (() => {
                                const matchedStr = typeof tag.matched === "string" ? tag.matched : String(tag.matched);
                                const scopeMatch = matchedStr.match(/^\[([^\]]+)\]\s*/);
                                const matchedScope = scopeMatch ? scopeMatch[1] : null;
                                const matchedText = scopeMatch ? matchedStr.replace(scopeMatch[0], "") : matchedStr;
                                const scopeColors = getScopeChipColors(matchedScope);

                                return (
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      backgroundColor: "#F8F9FF",
                                      borderRadius: "8px",
                                      border: "1px solid #E5E7FF",
                                    }}
                                  >
                                    <Typography sx={ { fontSize: 12, color: TEXT_SECONDARY, fontWeight: 600, mb: 0.5 }}>
                                      Matched signal
                                    </Typography>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: "wrap" }}>
                                      {matchedScope && (
                                        <Chip
                                          label={matchedScope}
                                          size="small"
                                          sx={{
                                            fontSize: 11,
                                            height: 22,
                                            borderRadius: "8px",
                                            backgroundColor: scopeColors.backgroundColor,
                                            color: scopeColors.color,
                                          }}
                                        />
                                      )}
                                      <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: PRIMARY_COLOR, wordBreak: "break-all" }}>
                                        {matchedText}
                                      </Typography>
                                    </Stack>
                                  </Box>
                                );
                              })()
                            )}
                            {Array.isArray(tag.signals) && tag.signals.length > 0 && (
                              <Accordion
                                defaultExpanded={idx === 0}
                                disableGutters
                                elevation={0}
                                sx={{
                                  mt: 0.5,
                                  border: `1px solid ${BORDER_COLOR}`,
                                  borderRadius: "10px",
                                  overflow: "hidden",
                                  "&:before": { display: "none" },
                                  backgroundColor: "#FAFAFA",
                                }}
                              >
                                <AccordionSummary
                                  expandIcon={<LayerdIcon icon={DETECTION_ICONS.chevronDown} size={22} color="neutral" />}
                                  sx={{
                                    cursor: "pointer",
                                    minHeight: 48,
                                    px: 2,
                                    py: 1.25,
                                    width: "100%",
                                    alignItems: "center",
                                    transition: "background-color 0.15s ease",
                                    "&:hover": { backgroundColor: "rgba(99, 113, 247, 0.06)" },
                                    "&.Mui-focusVisible": { backgroundColor: "rgba(99, 113, 247, 0.08)" },
                                    "& .MuiAccordionSummary-content": {
                                      flexGrow: 1,
                                      alignItems: "center",
                                      marginRight: 1,
                                      marginTop: 0,
                                      marginBottom: 0,
                                      overflow: "hidden",
                                    },
                                    "& .MuiAccordionSummary-expandIconWrapper": {
                                      flexShrink: 0,
                                    },
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: 14,
                                      lineHeight: "22px",
                                      fontWeight: 500,
                                      color: TEXT_PRIMARY,
                                    }}
                                  >
                                    Attack signals ({tag.signals.length})
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails
                                  sx={{
                                    px: 2,
                                    pt: 1.5,
                                    pb: 2.5,
                                    borderTop: `1px solid ${BORDER_COLOR}`,
                                    backgroundColor: "#F9FAFB",
                                  }}
                                >
                                  <Stack spacing={2}>
                                    {tag.signals.map((sig, si) => {
                                      const sigLabel = (sig.signal || "").trim();
                                      const scopeFromSignal = parseScopePrefix(sigLabel);
                                      const patternText =
                                        stripScopePrefix(sigLabel) || sigLabel || "—";
                                      const scopeColors = getScopeChipColors(scopeFromSignal || "pattern");
                                      const sevCfg = getSignalSeverityConfig(sig.severity);
                                      const hasValue = sig.signal_value && String(sig.signal_value).trim();
                                      return (
                                        <Box
                                          key={`intent-tag-${idx}-sig-${si}`}
                                          sx={{
                                            p: 2,
                                            backgroundColor: "#fff",
                                            borderRadius: "10px",
                                            border: `1px solid ${sevCfg.borderColor}`,
                                            boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
                                          }}
                                        >
                                          <Stack spacing={1.75}>
                                            <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center" sx={{ rowGap: 1 }}>
                                              <Chip
                                                label={scopeFromSignal || "Signal"}
                                                size="small"
                                                sx={{
                                                  fontSize: 11,
                                                  height: 24,
                                                  borderRadius: "8px",
                                                  backgroundColor: scopeColors.backgroundColor,
                                                  color: scopeColors.color,
                                                }}
                                              />
                                              {sig.severity ? (
                                                <Chip
                                                  label={sevCfg.label}
                                                  size="small"
                                                  sx={{
                                                    fontSize: 11,
                                                    height: 24,
                                                    borderRadius: "8px",
                                                    backgroundColor: sevCfg.bgColor,
                                                    color: sevCfg.color,
                                                  }}
                                                />
                                              ) : null}
                                            </Stack>
                                            <Typography
                                              sx={{
                                                fontSize: 13,
                                                color: PRIMARY_COLOR,
                                                wordBreak: "break-all",
                                                lineHeight: 1.45,
                                                letterSpacing: "-0.01em",
                                              }}
                                            >
                                              {patternText}
                                            </Typography>
                                            {sig.explanation ? (
                                              <Typography
                                                sx={{
                                                  fontSize: 13,
                                                  color: TEXT_SECONDARY,
                                                  lineHeight: 1.55,
                                                }}
                                              >
                                                {sig.explanation}
                                              </Typography>
                                            ) : null}
                                            {hasValue ? (
                                              <Box
                                                sx={{
                                                  mt: 0.5,
                                                  p: 1.5,
                                                  backgroundColor: "#F8FAFC",
                                                  borderRadius: "8px",
                                                  border: `1px dashed ${BORDER_COLOR}`,
                                                }}
                                              >
                                                <Typography
                                                  sx={{
                                                    fontSize: 11,
                                                    color: TEXT_SECONDARY,
                                                    fontWeight: 600,
                                                    mb: 1,
                                                    letterSpacing: "0.02em",
                                                  }}
                                                >
                                                  Matched value
                                                </Typography>
                                                <Typography
                                                  sx={{
                                                    fontSize: 12,
                                                    color: TEXT_PRIMARY,
                                                    wordBreak: "break-all",
                                                    lineHeight: 1.5,
                                                  }}
                                                >
                                                  {sig.signal_value}
                                                </Typography>
                                              </Box>
                                            ) : null}
                                          </Stack>
                                        </Box>
                                      );
                                    })}
                                  </Stack>
                                </AccordionDetails>
                              </Accordion>
                            )}
                            <Stack direction="row" flexWrap="wrap" gap={1}>
                              {tag.mitre && (
                                <Chip label={`MITRE: ${tag.mitre}`} size="small" sx={ { fontSize: 11, height: 20, borderRadius: "8px", backgroundColor: "#DBEAFE", color: "#1E40AF" }} />
                              )}
                              {tag.cwe && (
                                <Chip label={tag.cwe} size="small" sx={ { fontSize: 11, height: 20, borderRadius: "8px", backgroundColor: "#F3E8FF", color: "#7C3AED" }} />
                              )}
                            </Stack>
                          </Stack>
                        </Box>
                        );
                      })}
                    </Stack>
                  </SectionCard>
                ) : null}

                {isSmartParseEvent && (
                  <SectionCard icon={DETECTION_ICONS.info} title="Threat Intel">
                    <IpReputationPanel
                      showReportsSummary
                      ipReputation={{
                        abuseConfidenceScore: drawerData.threatIntel.abuseConfidenceScore,
                        ipAddress: drawerData.threatIntel.ipAddress !== "—"
                          ? drawerData.threatIntel.ipAddress
                          : null,
                        isp: drawerData.threatIntel.isp,
                        usageType: drawerData.threatIntel.usageType,
                        countryCode: drawerData.threatIntel.countryCode,
                        totalReports: drawerData.threatIntel.totalReports,
                      }}
                      showAbuseScore={drawerData.threatIntel.abuseConfidenceScore !== null}
                    />
                  </SectionCard>
                )}

                {isSmartParseEvent && drawerData.attackSignals.length > 0 && (
                  <SectionCard icon={DETECTION_ICONS.info} title="Attacks Signals">
                    <Stack spacing={1.5}>
                      {drawerData.attackSignals.map((signal, idx) => {
                        const severityConfig = getSignalSeverityConfig(signal.severity);
                        return (
                        <Box
                          key={`attack_signal_${idx}`}
                          sx={{
                            p: 1.5,
                            border: `1px solid ${BORDER_COLOR}`,
                            borderRadius: "8px",
                            transition: "all 0.18s ease",
                            "&:hover": {
                              borderColor: severityConfig.borderColor,
                              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                              transform: "translateY(-1px)",
                            },
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.5 }}>
                            <Box sx={{ flex: 1, pr: 1.5 }}>
                              <DrawerFieldStack
                                columns={1}
                                fields={[{ key: "signal", label: "Signal", value: signal.signal }]}
                              />
                            </Box>
                            <Chip
                              label={severityConfig.label}
                              size="small"
                              sx={{
                                width: "fit-content",
                                fontSize: 12,
                                fontWeight: 600,
                                color: severityConfig.color,
                                backgroundColor: severityConfig.bgColor,
                                border: `1px solid ${severityConfig.borderColor}`,
                                borderRadius: "999px",
                              }}
                            />
                          </Stack>
                          <DrawerFieldStack
                            columns={1}
                            fields={[
                              { key: "signalValue", label: "Signal Value", value: signal.signal_value, monospace: true },
                              { key: "explanation", label: "Explanation", value: signal.explanation },
                            ]}
                          />
                        </Box>
                      )})}
                    </Stack>
                  </SectionCard>
                )}

                {isSmartParseEvent && (remediationByIntent.length > 0 || remediationRecommendations.length > 0) && (
                  <SectionCard
                    icon={DETECTION_ICONS.info}
                    title={`Remediation Recommendations (${remediationByIntent.length > 0 ? remediationByIntent.length : remediationRecommendations.length})`}
                  >
                    {remediationByIntent.length > 0 ? (
                      <Stack spacing={1.5}>
                        {remediationByIntent.map((group, groupIdx) => (
                          <Box
                            key={`remediation_group_${group.intentId || group.intentName || groupIdx}`}
                            sx={{ p: 1.5, border: `1px solid ${BORDER_COLOR}`, borderRadius: "8px", backgroundColor: "#FAFAFA" }}
                          >
                            <Typography sx={{ ...baseFontStyle, fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY, mb: 0.75 }}>
                              {group.intentName || group.intentId || `Intent ${groupIdx + 1}`}
                            </Typography>
                            <DrawerNumberedList items={group.recommendations} variant="rem" />
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <DrawerNumberedList items={remediationRecommendations} variant="rem" />
                    )}
                  </SectionCard>
                )}

                <SectionCard icon={DETECTION_ICONS.detection} title="Raw Event" defaultExpanded={false}>
                  <SmartParseRawJsonViewer
                    data={drawerData.rawEventFiltered}
                    onCopy={handleCopy}
                    filename="event_detail.json"
                  />
                </SectionCard>

                <ViewSimilarEventsPanel
                  event={similarEventShape}
                  onFilterSimilar={handleFilterSimilarV3}
                />
    </WafEventDrawerShell>

    {snackOpen && (
      <Box
        sx={{
          position: "fixed",
          bottom: 80,
          right: 24,
          backgroundColor: TEXT_PRIMARY,
          color: "#fff",
          px: 2,
          py: 1,
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          zIndex: 1400,
        }}
      >
        <Typography sx={{ ...baseFontStyle, color: "#fff" }}>
          {snackMessage}
        </Typography>
      </Box>
    )}
    </>
  );
};

export default EventDetailDrawer;
